import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight, MonitorPlay, UsersThree, Check, ShieldCheck, Heartbeat, Pulse, ChartLineUp, CheckCircle,
  CalendarCheck, FileText, Brain, PlugsConnected, ArrowUUpLeft, Broadcast, Waveform, ClipboardText, Pill,
} from "@phosphor-icons/react";
import { FadeUp, Stagger, StaggerItem } from "@/lib/motion";
import { Container, Section, Overline, GradientBar, PrimaryLink, SecondaryLink, Eyebrow } from "@/lib/ui";
import { Seo } from "@/components/Seo";
import { DashboardMock } from "@/components/DashboardMock";
import { TopPrograms } from "@/components/TopPrograms";
import { SmartImage } from "@/components/SmartImage";
import { RevealText, Parallax, CountUp, ScrollScale, MagneticHover } from "@/components/ScrollFX";
import { track } from "@/lib/analytics";
import { PROGRAMS, OPPORTUNITY_STATS, MANIFESTO, CARE_CONTINUUM, PILLARS, HOW_IT_WORKS, EHRS, HIE_HIGHLIGHTS, HIE_CAPABILITIES, HIE_ADT_EVENTS } from "@/lib/zocData";

const IMG = {
  care: "https://images.unsplash.com/photo-1758691463198-dc663b8a64e4?q=80&w=1000&auto=format&fit=crop",
};
const PILLAR_ICONS = { platform: MonitorPlay, services: UsersThree, "project-management": ClipboardText };
const PROGRAM_ICONS = { awv: CalendarCheck, apcm: ShieldCheck, ccm: Pulse, rpm: Heartbeat, rtm: ChartLineUp, acp: FileText, cocm: Brain, tcm: ArrowUUpLeft };
const HIE_ICONS = { 0: Broadcast, 1: Waveform, 2: ClipboardText, 3: Pill };

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-white">
      <Seo
        path="/"
        title="Care Management Software & Coordination Services"
        description="ZOC Medical pairs a care-management platform with a clinical coordination team to run APCM, RPM, CCM, RTM, CoCM, TCM, AWV & Advance Care Planning — with HIE integration for real-time hospital data and post-discharge transitions."
        keywords="care management software, remote patient monitoring, chronic care management, transition care management, TCM, APCM, RPM, CCM, RTM, CoCM, HIE integration, ADT alerts, Medicare care coordination"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Medicare Care Management",
          "name": "ZOC Care Management Suite — Platform + Coordination Services",
          "description": "Software platform and managed clinical coordination team for medical practices — covering APCM, RPM, CCM, RTM, CoCM, TCM, Annual Wellness Visits and Advance Care Planning, with HIE integration for real-time hospital data.",
          "provider": {
            "@type": "MedicalOrganization",
            "name": "ZOC Medical Inc",
            "url": "https://www.zocmedical.com/",
            "logo": "https://www.zocmedical.com/zoc-mark.png",
          },
          "areaServed": { "@type": "Country", "name": "United States" },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Care Management Programs",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Annual Wellness Visits (AWV)" } },
              { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Advanced Primary Care Management (APCM)" } },
              { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Remote Patient Monitoring (RPM)" } },
              { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Chronic Care Management (CCM)" } },
              { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Remote Therapeutic Monitoring (RTM)" } },
              { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Advance Care Planning (ACP)" } },
              { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Collaborative Care Management (CoCM)" } },
              { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Transition Care Management (TCM)" } },
            ],
          },
        }}
      />
      <Hero />
      <Pillars />
      <EHRSection />
      <HIESection />
      <WhyItMatters />
      <ProgramsGrid />
      <TopPrograms />
      <HowItWorks />
      <Values />
      <Continuum />
      <FinalCTA />
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-white pt-16 sm:pt-20">
      <div className="absolute inset-0 grid-pattern" />
      <Container className="relative pb-16 sm:pb-20">
        <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] items-center pt-8 lg:pt-12">
          <div>
            <FadeUp>
              <Eyebrow>Platform + Managed Care Services</Eyebrow>
            </FadeUp>
            <FadeUp delay={0.05}>
              <h1 className="mt-6 font-display text-[2.7rem] leading-[1.02] sm:text-6xl lg:text-[4.4rem] font-extrabold text-[#0B2A4E]">
                <RevealText as="span" text="The software and the" stagger={0.06} />{" "}
                <RevealText as="span" text="care team" delay={0.25} stagger={0.06} gradient />{" "}
                <RevealText as="span" text="behind better care." delay={0.4} stagger={0.06} />
              </h1>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-6 max-w-xl text-lg text-slate-600 leading-relaxed">
                ZOC Medical pairs a purpose-built care-management platform with a dedicated clinical
                coordination team — so your practice can run every Medicare care program, from Annual
                Wellness Visits to remote monitoring, and stay effortlessly compliant.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-3">
                <MagneticHover><PrimaryLink to="/contact" testid="hero-demo-cta">Book a demo</PrimaryLink></MagneticHover>
                <MagneticHover><SecondaryLink to="/platform" testid="hero-platform-cta">See how it works</SecondaryLink></MagneticHover>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500">
                {["AWV", "ACP", "CoCM", "CCM", "APCM", "RPM", "RTM", "TCM"].map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5">
                    <CheckCircle weight="fill" className="text-zoc-green" size={15} /> {c}
                  </span>
                ))}
              </div>
            </FadeUp>
          </div>

          {/* Product visual */}
          <FadeUp delay={0.15} className="relative">
            <motion.div style={{ y }} className="relative zoc-glow">
              <DashboardMock />
            </motion.div>
          </FadeUp>
        </div>
      </Container>
    </section>
  );
}

function Pillars() {
  return (
    <Section data-testid="pillars-section" className="bg-slate-50 border-y border-slate-200">
      <Container>
        <FadeUp className="max-w-2xl">
          <Overline>Two ways we help</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0B2A4E]">
            Software and services — together or on their own.
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">
            Some practices want the platform to run care management in-house. Others want us to run it for them.
            With ZOC Medical you can do either — or both.
          </p>
        </FadeUp>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {PILLARS.map((p, i) => {
            const Icon = PILLAR_ICONS[p.key];
            return (
              <FadeUp key={p.key} delay={i * 0.06}>
                <Link
                  to={p.href}
                  data-testid={`pillar-${p.key}`}
                  className="group relative block h-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="absolute inset-x-0 top-0 h-1 zoc-gradient-bar opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <span className="grid place-items-center h-12 w-12 rounded-xl zoc-chip text-white shadow-sm">
                    <Icon size={24} weight="duotone" />
                  </span>
                  <p className="mt-6 text-xs font-bold uppercase tracking-[0.16em] text-zoc-teal">{p.kicker}</p>
                  <h3 className="mt-2 font-display text-xl sm:text-2xl font-bold text-[#0B2A4E]">{p.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.body}</p>
                  <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2.5">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check weight="bold" className="text-zoc-green shrink-0 mt-0.5" size={16} /> {pt}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B2A4E] transition-all duration-200 group-hover:gap-2.5">
                    Learn more <ArrowRight weight="bold" size={15} />
                  </span>
                </Link>
              </FadeUp>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}

function WhyItMatters() {
  return (
    <Section data-testid="why-section" className="bg-white">
      <Container>
        <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] items-start">
          <FadeUp>
            <Overline>Why it matters</Overline>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0B2A4E]">
              The biggest gap in care is between the visits.
            </h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">
              Most eligible patients are never enrolled in the ongoing care they qualify for — so chronic
              conditions drift and avoidable crises happen. We close that gap, one patient at a time.
            </p>
            <div className="mt-6"><GradientBar /></div>
          </FadeUp>

          <Stagger className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {OPPORTUNITY_STATS.map((s, i) => {
              // Parse leading numeric value for CountUp; keep the display suffix (%, /7, etc.)
              const m = /^([<>~]?\s*)(\d[\d,\.]*)(.*)$/.exec(s.value);
              return (
                <StaggerItem key={i}>
                  <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                    <p className="font-display text-4xl sm:text-5xl font-extrabold zoc-gradient-text tabular-nums">
                      {m ? (
                        <>
                          {m[1]}
                          <CountUp value={Number(m[2].replace(/,/g, ""))} />
                          {m[3]}
                        </>
                      ) : (
                        s.value
                      )}
                    </p>
                    <p className="mt-3 text-sm text-slate-600 leading-snug">{s.label}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </Container>
    </Section>
  );
}

function ProgramsGrid() {
  return (
    <Section data-testid="programs-section" className="bg-slate-50 border-y border-slate-200">
      <Container>
        <FadeUp className="max-w-2xl">
          <Overline>The programs</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0B2A4E]">
            Eight Medicare programs. One continuum of care.
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">
            Software <span className="font-semibold text-[#0B2A4E]">and</span> managed care coordination for every
            program — from Annual Wellness Visits and behavioral health to remote monitoring and post-discharge transitions.
          </p>
        </FadeUp>
        <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PROGRAMS.map((p) => {
            const Icon = PROGRAM_ICONS[p.slug];
            return (
              <StaggerItem key={p.slug}>
                <Link
                  to={`/programs/${p.slug}`}
                  data-testid={`program-card-${p.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                >
                  <span className="grid place-items-center h-11 w-11 rounded-xl text-white shadow-sm" style={{ backgroundColor: p.color }}>
                    <Icon size={22} weight="duotone" />
                  </span>
                  <p className="mt-5 font-display text-xl font-bold text-[#0B2A4E]">{p.code}</p>
                  <p className="text-sm font-medium text-slate-500">{p.name}</p>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{p.tagline}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-zoc-teal transition-all duration-200 group-hover:gap-2.5">
                    Details <ArrowRight weight="bold" size={14} />
                  </span>
                </Link>
              </StaggerItem>
            );
          })}
        </Stagger>
      </Container>
    </Section>
  );
}

function HowItWorks() {
  return (
    <Section data-testid="how-section" className="bg-white">
      <Container>
        <FadeUp className="max-w-2xl">
          <Overline>How the service works</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0B2A4E]">
            We handle the coordination. You keep the relationship.
          </h2>
        </FadeUp>
        <div className="mt-12 grid gap-4 md:grid-cols-5">
          {HOW_IT_WORKS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ type: "spring", stiffness: 100, damping: 20, delay: i * 0.06 }}
              data-testid={`how-step-${s.n}`}
              className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <span className="grid place-items-center h-9 w-9 rounded-lg bg-slate-100 font-display text-sm font-bold text-[#0B2A4E]">{s.n}</span>
              <h3 className="mt-4 font-display text-lg font-bold text-[#0B2A4E]">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
        <FadeUp className="mt-8">
          <PrimaryLink to="/services" testid="how-services-link">Explore our care services</PrimaryLink>
        </FadeUp>
      </Container>
    </Section>
  );
}

function Values() {
  return (
    <Section data-testid="values-section" className="bg-slate-50 border-y border-slate-200">
      <Container>
        <FadeUp className="max-w-2xl">
          <Overline>What we believe</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0B2A4E]">
            Why continuous care changes everything.
          </h2>
        </FadeUp>
        <div className="mt-12 grid gap-x-10 gap-y-10 md:grid-cols-2">
          {MANIFESTO.map((m, i) => (
            <FadeUp key={m.n} delay={i * 0.05}>
              <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm h-full">
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-3xl font-extrabold zoc-gradient-text">{m.n}</span>
                  <h3 className="font-display text-2xl font-bold text-[#0B2A4E]">{m.title}</h3>
                </div>
                <p className="mt-3 text-slate-600 leading-relaxed">{m.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </Container>
    </Section>
  );
}

function Continuum() {
  return (
    <Section data-testid="continuum-section" className="bg-white">
      <Container>
        <div className="grid gap-12 lg:grid-cols-2 items-center">
          <FadeUp>
            <Overline>The care continuum</Overline>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0B2A4E]">
              One patient. A lifetime of coordinated care.
            </h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">
              The right program follows each patient through every stage of their health journey —
              one relationship, sustained.
            </p>
            <Stagger className="mt-8 space-y-2">
              {CARE_CONTINUUM.map((step, i) => (
                <StaggerItem key={step}>
                  <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-5 py-3.5 shadow-sm">
                    <span className="font-display text-sm font-bold text-zoc-teal w-8">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[#0B2A4E] font-medium">{step}</span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </FadeUp>
          <FadeUp delay={0.1} className="relative">
            <div className="relative zoc-glow">
              <ScrollScale className="overflow-hidden rounded-2xl border border-slate-200 shadow-xl">
                <SmartImage
                  src={IMG.care}
                  alt="Clinician using ZOC Medical care coordination platform on a laptop"
                  width={1000}
                  height={672}
                  className="w-full h-[480px] object-cover"
                  data-testid="continuum-image"
                />
              </ScrollScale>
              <div className="absolute -bottom-5 -left-5 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-lg">
                <p className="font-display text-3xl font-extrabold text-zoc-teal"><CountUp value={50} suffix="%" /></p>
                <p className="text-xs text-slate-500 max-w-[140px]">up to — fewer hospitalizations for managed patients</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </Container>
    </Section>
  );
}

function HIELiveTicker() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) return;
    const t = setInterval(() => setI((n) => (n + 1) % HIE_ADT_EVENTS.length), 2600);
    return () => clearInterval(t);
  }, []);
  const visible = [0, 1, 2].map((k) => HIE_ADT_EVENTS[(i + k) % HIE_ADT_EVENTS.length]);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Live from the exchange</p>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#D97706]/12 px-2 py-0.5 text-[10px] font-bold text-[#B45309]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#D97706] animate-pulse" /> LIVE
        </span>
      </div>
      <div className="mt-4 space-y-2">
        <AnimatePresence mode="popLayout" initial={false}>
          {visible.map((e) => (
            <motion.div
              key={e.patient + e.event}
              layout
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5"
            >
              <span className="h-2 w-2 rounded-full shrink-0" style={{ background: e.tone }} />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-[#0B2A4E] leading-tight truncate">{e.event}</p>
                <p className="text-[11px] text-slate-500 leading-tight truncate">{e.patient}</p>
              </div>
              <span className="rounded-full px-2 py-0.5 text-[10px] font-bold shrink-0" style={{ background: `${e.tone}15`, color: e.tone }}>{e.tag}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function HIESection() {
  return (
    <Section data-testid="hie-section" className="bg-slate-50 border-y border-slate-200">
      <Container>
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr] items-start">
          <FadeUp>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#D97706]/25 bg-white px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-[#D97706] shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-[#D97706] animate-pulse" />
              NEW · Health Information Exchange
            </div>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.03] font-bold text-[#0B2A4E]">
              Hospital data. <span className="zoc-gradient-text">The second it happens.</span>
            </h2>
            <p className="mt-5 max-w-xl text-slate-600 text-lg leading-relaxed">
              ZOC connects to every major U.S. Health Information Exchange — so an admit or discharge feed lands
              in your console the moment it happens, and a full longitudinal record is one click away. That means
              <span className="font-semibold text-[#0B2A4E]"> Transition Care Management (TCM 99495/99496)</span> starts on time, every time.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-3">
              {HIE_HIGHLIGHTS.map((h) => (
                <div key={h.stat} data-testid={`hie-highlight-${h.stat}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="font-display text-xl font-extrabold text-[#0B2A4E] leading-none">{h.stat}</p>
                  <p className="mt-2 text-xs text-slate-500 leading-snug">{h.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8"><HIELiveTicker /></div>
          </FadeUp>

          <Stagger className="grid gap-4 sm:grid-cols-2">
            {HIE_CAPABILITIES.map((cap, i) => {
              const Icon = HIE_ICONS[i] || Broadcast;
              return (
                <StaggerItem key={cap.title}>
                  <div data-testid={`hie-cap-${i}`} className="group h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
                    <span className="grid place-items-center h-11 w-11 rounded-xl text-white shadow-sm" style={{ background: "linear-gradient(135deg, #D97706, #F59E0B)" }}>
                      <Icon size={22} weight="duotone" />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-bold text-[#0B2A4E]">{cap.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{cap.body}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>

        <FadeUp className="mt-10">
          <div className="flex flex-wrap items-center gap-3">
            <PrimaryLink to="/programs/tcm" testid="hie-tcm-cta">See how TCM works</PrimaryLink>
            <SecondaryLink to="/contact" testid="hie-demo-cta">Talk to us about your HIE</SecondaryLink>
          </div>
        </FadeUp>
      </Container>
    </Section>
  );
}

function EHRSection() {
  return (
    <Section data-testid="ehr-section" className="bg-white">
      <Container>
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-[#e6f4f2] p-8 sm:p-12 lg:p-14 shadow-[0_1px_0_0_rgba(15,23,42,0.04),0_20px_60px_-30px_rgba(18,140,140,0.25)]">
          {/* Highlight accents */}
          <div className="absolute inset-x-0 top-0 h-1 zoc-gradient-bar" />
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-zoc-teal/10 blur-3xl" />
          <div className="pointer-events-none absolute -left-16 -bottom-16 h-64 w-64 rounded-full bg-zoc-green/10 blur-3xl" />

          <div className="relative grid gap-12 lg:grid-cols-[0.9fr_1.1fr] items-center">
            <FadeUp>
              <div className="inline-flex items-center gap-2 rounded-full border border-zoc-teal/25 bg-white px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-zoc-teal shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-zoc-green animate-pulse" />
                No rip-and-replace
              </div>
              <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.03] font-bold text-[#0B2A4E]">
                Works with <span className="zoc-gradient-text">all major EHRs.</span>
              </h2>
              <p className="mt-5 max-w-md text-slate-600 text-lg leading-relaxed">
                Plug ZOC alongside the systems you already use. We confirm the specifics for your setup during onboarding — so care management runs inside your existing workflow from day one.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2.5">
                  <span className="grid place-items-center h-9 w-9 rounded-lg bg-white border border-slate-200 shadow-sm">
                    <PlugsConnected size={18} weight="duotone" className="text-zoc-teal" />
                  </span>
                  <div>
                    <p className="text-2xl font-display font-extrabold text-[#0B2A4E] leading-none">12+</p>
                    <p className="text-xs text-slate-500 leading-tight">Certified EHRs</p>
                  </div>
                </div>
                <div className="h-8 w-px bg-slate-200" />
                <div>
                  <p className="text-2xl font-display font-extrabold text-[#0B2A4E] leading-none">2 weeks</p>
                  <p className="text-xs text-slate-500 leading-tight">Typical time to first data</p>
                </div>
              </div>
            </FadeUp>

            <Stagger className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3">
              {EHRS.map((ehr) => (
                <StaggerItem key={ehr}>
                  <div
                    data-testid={`ehr-${ehr}`}
                    className="group flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm transition-all duration-200 hover:border-zoc-teal/50 hover:shadow-md hover:-translate-y-0.5"
                  >
                    <span className="grid place-items-center h-6 w-6 rounded-md bg-zoc-teal/10 shrink-0 transition-colors group-hover:bg-zoc-teal/20">
                      <Check weight="bold" size={13} className="text-zoc-teal" />
                    </span>
                    <span className="text-sm font-semibold text-[#0B2A4E] leading-tight">{ehr}</span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>

          <FadeUp>
            <p className="relative mt-9 text-sm text-slate-500">Don&rsquo;t see yours? We integrate with virtually any certified EHR — <Link to="/contact" className="font-semibold text-zoc-teal hover:text-[#0B2A4E] transition-colors">just ask</Link>.</p>
          </FadeUp>
        </div>
      </Container>
    </Section>
  );
}

function FinalCTA() {
  return (
    <Section data-testid="final-cta" className="bg-white">
      <Container>
        <div className="relative overflow-hidden rounded-3xl bg-[#0B2A4E] px-8 py-14 sm:px-14 sm:py-20">
          <div className="absolute inset-x-0 top-0 h-1 zoc-gradient-bar" />
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-zoc-teal/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zoc-green">Your practice, transformed</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-white">
              Let us run care management, so you can run your practice.
            </h2>
            <p className="mt-4 text-slate-300 text-lg leading-relaxed">
              Give every patient continuous, coordinated care — with the platform and the people to deliver it.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="cta-demo" onClick={() => track("demo_click", { source: "home_final_cta" })} className="group inline-flex items-center gap-2 rounded-full bg-white text-[#0B2A4E] px-6 py-3 font-semibold transition-transform duration-200 hover:-translate-y-0.5">
                Book a demo <ArrowRight weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link to="/programs" data-testid="cta-programs" className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-6 py-3 font-medium transition-colors duration-200 hover:bg-white/10">
                Explore programs
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}
